import React from "react";
import {
  Box,
  Paper,
  Chip,
  Typography,
  Button,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import { C } from "./constants";
import SvgIcon from "../ipd-billing/SvgIcon";

export const PAGE_PAD_SX = { p: "28px 32px" };

export function OtPage({ children, sx }) {
  return <Box sx={{ ...PAGE_PAD_SX, ...sx }}>{children}</Box>;
}

export const eyebrowSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: C.primary,
};

export const titleSx = {
  mt: "6px",
  fontSize: 24,
  fontWeight: 700,
  color: C.text,
};

export const subtitleSx = {
  mt: "6px",
  fontSize: 13,
  color: C.textSub,
};

export const CATEGORY_TONES = {
  Scheduling: { bg: "#e0e7ff", color: "#4338ca" },
  Assessment: { bg: "#fff8e1", color: "#856404" },
  "Peri-Operative": { bg: "#E9F5FE", color: C.primary },
  Support: { bg: "#d1f2db", color: "#0f766e" },
};

export const SLOT_TONES = {
  elective: { bg: "#E9F5FE", border: "#b6d4fe", color: C.primary },
  emergency: { bg: "#fdecea", border: "#f5c2c7", color: C.danger },
  daycare: { bg: "#e0e7ff", border: "#c7d2fe", color: "#4338ca" },
};

const KPI_TONES = {
  primary: { bg: "#E9F5FE", color: C.primary },
  success: { bg: "#d1f2db", color: C.success },
  info: { bg: "#e0e7ff", color: "#4338ca" },
  danger: { bg: "#fdecea", color: C.danger },
};

export function caseStatusTone(status) {
  if (status === "Ready") return { bg: "#d1f2db", color: C.success };
  if (status === "PAC pending") return { bg: "#fff8e1", color: "#856404" };
  return { bg: "#fdecea", color: C.danger };
}

const sectionCardSx = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: "16px",
  boxShadow: "0 2px 8px rgba(13,35,63,0.06)",
};

export function SectionCard({ children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...sectionCardSx, ...sx }}>
      {children}
    </Paper>
  );
}

export function CategoryBadge({ category }) {
  const tone = CATEGORY_TONES[category] || CATEGORY_TONES.Support;
  return (
    <Chip
      label={category}
      size="small"
      sx={{
        height: "auto",
        borderRadius: 999,
        px: 0.5,
        py: 0.25,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        bgcolor: tone.bg,
        color: tone.color,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

export function StatusPill({ label, tone }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: "auto",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        bgcolor: tone.bg,
        color: tone.color,
        "& .MuiChip-label": { px: 1, py: 0.25 },
      }}
    />
  );
}

export function KpiTile({ icon, label, value, tone = "primary" }) {
  const t = KPI_TONES[tone] || KPI_TONES.primary;
  return (
    <Paper elevation={0} sx={{ ...sectionCardSx, p: 1.75, display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          bgcolor: t.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <SvgIcon name={icon} size={18} color={t.color} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

export function OtButton({ children, onClick, variant = "contained", sx, startIcon, ...rest }) {
  const isText = variant === "text";
  const isOutlined = variant === "outlined";
  return (
    <Button
      onClick={onClick}
      disableElevation
      variant={isText ? "text" : isOutlined ? "outlined" : "contained"}
      startIcon={startIcon}
      sx={{
        textTransform: "none",
        fontWeight: 600,
        fontSize: 13,
        borderRadius: "8px",
        ...(isText
          ? { color: C.primary, p: 0, minWidth: 0, "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }
          : isOutlined
            ? { color: C.text, borderColor: C.border, bgcolor: "#fff", "&:hover": { borderColor: C.primary, bgcolor: "#fff" } }
            : { bgcolor: C.primary, color: "#fff", px: 2, py: 1, "&:hover": { bgcolor: C.primaryHover } }),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

export function OtLinkButton({ children, onClick, sx, startIcon, ...rest }) {
  return (
    <OtButton variant="text" onClick={onClick} startIcon={startIcon} sx={{ fontSize: 13, ...sx }} {...rest}>
      {children}
    </OtButton>
  );
}

export function OtCardButton({ children, onClick, sx }) {
  return (
    <Button
      onClick={onClick}
      fullWidth
      sx={{
        display: "flex",
        textTransform: "none",
        textAlign: "left",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${C.border}`,
        bgcolor: "#fff",
        color: C.text,
        boxShadow: "none",
        "&:hover": { bgcolor: "#fff", borderColor: C.primary, boxShadow: "none" },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

export function OtListButton({ children, onClick, endIcon }) {
  return (
    <Button
      onClick={onClick}
      fullWidth
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        textTransform: "none",
        textAlign: "left",
        p: "10px 12px",
        borderRadius: "8px",
        border: `1px solid ${C.border}`,
        bgcolor: "#fff",
        color: C.text,
        boxShadow: "none",
        "&:hover": { bgcolor: "#fff", borderColor: C.primary },
      }}
      endIcon={endIcon}
    >
      {children}
    </Button>
  );
}

export function OtDataTable({ columns, rows, getRowKey }) {
  return (
    <TableContainer sx={{ border: `1px solid ${C.border}`, borderRadius: "10px" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f8f9fa" }}>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: C.textSub,
                  py: 1.25,
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={getRowKey(row)} sx={{ "& td": { fontSize: 13, py: 1.5, borderColor: C.border } }}>
              {columns.map((col) => (
                <TableCell key={col.key} sx={col.cellSx?.(row) || {}}>
                  {col.render ? col.render(row) : row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function OtProgressBar({ value, phases, activeIndex }) {
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          mt: 0.75,
          height: 8,
          borderRadius: 999,
          bgcolor: "#e9ecef",
          "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: C.primary },
        }}
      />
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        {phases.map((p, i) => (
          <Typography key={p} sx={{ fontSize: 9, color: i <= activeIndex ? C.text : C.textSub }}>
            {p}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export { Box, Paper, Chip, Typography, Button, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow };
